import { renderer } from "../rendering/RendererGlobals";
import { WebGLRenderTarget, Scene, PerspectiveCamera, Mesh, PlaneGeometry, ShaderMaterial, Vector2, SRGBColorSpace } from "three";
import { mainScene } from "../rendering/mainScene";
import { camera } from "../rendering/camera";
import { ScenePostprocessingRenderer } from "../rendering/postprocessing/ScenePostprocessingRenderer";
import { registerLiveViewStream, MIN_LIVE_VIEW_FPS, MAX_LIVE_VIEW_FPS } from "../multiplayer/mediaStream/LiveViewQuality";
const tmpVector2 = new Vector2();
const LIVE_VIEW_VERTICAL_FOV = 45;
const MIN_LIVE_VIEW_WIDTH = 1280;
const MIN_LIVE_VIEW_HEIGHT = 720;
const MAX_LIVE_VIEW_WIDTH = 1920;
const MAX_LIVE_VIEW_HEIGHT = 1080;
const MAX_LOCAL_VIEW_WIDTH = 2560;
const MAX_LOCAL_VIEW_HEIGHT = 1440;
const LOCAL_UPGRADE_STABLE_MS = 3000;
const LOCAL_UPGRADE_STEP_MS = 1000;
const MIN_AUTO_PIXEL_RATIO = 0.05;
const MIN_CAPTURE_QUALITY = 0.35;
const CAPTURE_QUALITY_DECREASE_THRESHOLD_MS = 5;
const CAPTURE_QUALITY_INCREASE_THRESHOLD_MS = 2;
const VERTEX_SHADER = `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;
const FRAGMENT_SHADER = `
varying vec2 vUv;

uniform sampler2D map;

void main() {
  gl_FragColor = vec4(texture2D(map, vUv).rgb, 1.0);
  #include <colorspace_fragment>
}
`;
export class BystanderView {
    // Sample every XR frame, not just the throttled Live View frames. Submission time alone cannot detect GPU stalls.
    updateXRFrameTiming(frameAt, renderDuration, targetFPS) {
        const budget = 1000 / targetFPS;
        const interval = frameAt - this.lastXRFrameAt;
        const hasPreviousFrame = this.lastXRFrameAt > 0;
        this.lastXRFrameAt = frameAt;
        this.xrFrameOverBudget = hasPreviousFrame && (interval > budget * 1.15 || renderDuration > budget * 0.4);
        this.updateFrameRate(frameAt, this.xrFrameOverBudget, hasPreviousFrame && !this.xrFrameOverBudget);
        if (!this.showLocally || !hasPreviousFrame || interval > budget * 1.15 ||
            renderDuration > budget * 0.4 || this.meanRenderDuration >= CAPTURE_QUALITY_INCREASE_THRESHOLD_MS) {
            this.stableFramesSince = 0;
            this.lastResolutionUpgradeAt = frameAt;
            if (interval > budget * 1.15 || renderDuration > budget * 0.4 ||
                this.meanRenderDuration >= CAPTURE_QUALITY_INCREASE_THRESHOLD_MS) {
                this.localResolutionBoost = 0;
            }
            return;
        }
        if (this.stableFramesSince === 0)
            this.stableFramesSince = frameAt;
        if (frameAt - this.stableFramesSince >= LOCAL_UPGRADE_STABLE_MS &&
            frameAt - this.lastResolutionUpgradeAt >= LOCAL_UPGRADE_STEP_MS) {
            this.localResolutionBoost = Math.min(1, this.localResolutionBoost + 0.125);
            this.lastResolutionUpgradeAt = frameAt;
        }
    }
    constructor() {
        this.postprocessingRenderer = new ScenePostprocessingRenderer(renderer);
        this.renderTarget = new WebGLRenderTarget(1, 1, {
            depthBuffer: true,
            stencilBuffer: false,
            generateMipmaps: false,
            colorSpace: SRGBColorSpace
        });
        this.scene = new Scene();
        this.captureRenderTarget = new WebGLRenderTarget(1, 1, {
            depthBuffer: false,
            stencilBuffer: false,
            generateMipmaps: false,
            colorSpace: SRGBColorSpace
        });
        this.screenCamera = new PerspectiveCamera(45, 1, 1, 1000);
        this.viewCamera = new PerspectiveCamera();
        this.captureCanvas = document.createElement("canvas");
        this.captureContext = this.captureCanvas.getContext("2d");
        this.captureBufferCanvas = document.createElement("canvas");
        this.captureBufferContext = this.captureBufferCanvas.getContext("2d");
        this.capturePixels = new Uint8Array();
        this.captureReadbackPending = false;
        this.captureQuality = 1;
        this.captureReadbackGeneration = 0;
        this.meanRenderDuration = 0;
        this.lastFrameAt = 0;
        this.viewportWidth = 0;
        this.viewportHeight = 0;
        this.localResolutionBoost = 0;
        this.lastXRFrameAt = 0;
        this.stableFramesSince = 0;
        this.lastResolutionUpgradeAt = 0;
        this.showLocally = false;
        this.streaming = false;
        this.liveViewFPS = MAX_LIVE_VIEW_FPS;
        this.lastFPSAdjustmentAt = 0;
        this.lastFramePressureAt = 0;
        this.xrFrameOverBudget = false;
        const material = new ShaderMaterial({
            vertexShader: VERTEX_SHADER,
            fragmentShader: FRAGMENT_SHADER,
            depthWrite: false,
            depthTest: false
        });
        material.uniforms.map = { value: this.renderTarget.texture };
        const quad = new Mesh(new PlaneGeometry(2, 2), material);
        quad.matrixAutoUpdate = false;
        quad.frustumCulled = false;
        this.scene.add(quad);
    }
    render(showLocally, updateCaptureStream, usePostEffects, gtaoSettings, bloomSettings, bloomResolutionScale = 0.25, smaaQuality = "medium", useBloom = true, useGtao = true) {
        const session = renderer.xr.getSession();
        if (!session)
            return;
        this.showLocally = showLocally;
        this.streaming = updateCaptureStream;
        if (!showLocally)
            this.localResolutionBoost = 0;
        const now = performance.now();
        if (now - this.lastFrameAt < this.getFrameInterval())
            return;
        if (this.captureReadbackPending && !showLocally)
            return;
        this.lastFrameAt = now;
        const currentRenderTarget = renderer.getRenderTarget();
        const xrEnabled = renderer.xr.enabled;
        try {
            const renderStartedAt = performance.now();
            this.renderMonoView(usePostEffects, gtaoSettings, bloomSettings, bloomResolutionScale, smaaQuality, useBloom, useGtao);
            this.updateCaptureQuality(performance.now() - renderStartedAt);
            if (updateCaptureStream && !this.captureReadbackPending) {
                this.copyRenderTargetToCaptureCanvas();
            }
            if (showLocally) {
                this.renderTexture();
            }
        }
        finally {
            renderer.xr.enabled = xrEnabled;
            renderer.setRenderTarget(currentRenderTarget);
        }
    }
    updateRenderTargetSize(width, height) {
        if (width <= 0 || height <= 0)
            return;
        this.viewportWidth = width;
        this.viewportHeight = height;
        this.updateLiveViewSize(width, height);
    }
    captureStream() {
        this.streaming = true;
        const rendererSize = renderer.getSize(tmpVector2);
        this.updateLiveViewSize(this.viewportWidth || rendererSize.x, this.viewportHeight || rendererSize.y);
        const { width, height } = this.getCaptureSize();
        this.captureCanvas.width = width;
        this.captureCanvas.height = height;
        this.captureBufferCanvas.width = width;
        this.captureBufferCanvas.height = height;
        const stream = this.captureCanvas.captureStream(MAX_LIVE_VIEW_FPS);
        registerLiveViewStream(stream);
        return stream;
    }
    releaseResources() {
        this.postprocessingRenderer.dispose();
        this.captureReadbackGeneration++;
        this.renderTarget.dispose();
        this.captureRenderTarget.dispose();
        this.capturePixels = new Uint8Array();
        this.captureImageData = undefined;
        this.captureCanvas.width = 0;
        this.captureCanvas.height = 0;
        this.captureBufferCanvas.width = 0;
        this.captureBufferCanvas.height = 0;
        this.captureQuality = 1;
        this.meanRenderDuration = 0;
        this.lastFrameAt = 0;
        this.localResolutionBoost = 0;
        this.lastXRFrameAt = 0;
        this.stableFramesSince = 0;
        this.lastResolutionUpgradeAt = 0;
        this.showLocally = false;
        this.streaming = false;
        this.liveViewFPS = MAX_LIVE_VIEW_FPS;
        this.lastFPSAdjustmentAt = 0;
        this.lastFramePressureAt = 0;
        this.xrFrameOverBudget = false;
    }
    renderMonoView(usePostEffects, gtaoSettings, bloomSettings, bloomResolutionScale = 0.25, smaaQuality = "medium", useBloom = true, useGtao = true) {
        const rendererSize = renderer.getSize(tmpVector2);
        this.updateLiveViewSize(this.viewportWidth || rendererSize.x, this.viewportHeight || rendererSize.y);
        renderer.xr.enabled = false;
        renderer.setRenderTarget(this.renderTarget);
        camera.perspectiveCam.matrixWorld.decompose(this.viewCamera.position, this.viewCamera.quaternion, this.viewCamera.scale);
        this.viewCamera.fov = LIVE_VIEW_VERTICAL_FOV;
        this.viewCamera.near = camera.perspectiveCam.near;
        this.viewCamera.far = camera.perspectiveCam.far;
        this.viewCamera.aspect = this.renderTarget.width / this.renderTarget.height;
        this.viewCamera.updateProjectionMatrix();
        this.viewCamera.updateMatrixWorld(true);
        const matrixWorldAutoUpdate = mainScene.matrixWorldAutoUpdate;
        const shadowAutoUpdate = renderer.shadowMap.autoUpdate;
        const shadowNeedsUpdate = renderer.shadowMap.needsUpdate;
        try {
            // The XR pass immediately before this pass has already updated the entire hierarchy and shadows.
            mainScene.matrixWorldAutoUpdate = false;
            renderer.shadowMap.autoUpdate = false;
            renderer.shadowMap.needsUpdate = false;
            this.postprocessingRenderer.render(mainScene, this.viewCamera, usePostEffects, gtaoSettings, bloomSettings, bloomResolutionScale, smaaQuality, useBloom, useGtao);
        }
        finally {
            mainScene.matrixWorldAutoUpdate = matrixWorldAutoUpdate;
            renderer.shadowMap.autoUpdate = shadowAutoUpdate;
            renderer.shadowMap.needsUpdate = shadowNeedsUpdate;
        }
    }
    updateLiveViewSize(viewportWidth, viewportHeight) {
        if (viewportWidth <= 0 || viewportHeight <= 0)
            return;
        if (this.streaming) {
            viewportWidth = Math.max(MIN_LIVE_VIEW_WIDTH, viewportWidth);
            viewportHeight = (viewportWidth * MIN_LIVE_VIEW_HEIGHT) / MIN_LIVE_VIEW_WIDTH;
        }
        const pixelRatio = this.streaming ? Math.max(1, renderer.getPixelRatio()) : renderer.getPixelRatio() * this.captureQuality;
        const maxWidth = MAX_LIVE_VIEW_WIDTH + (MAX_LOCAL_VIEW_WIDTH - MAX_LIVE_VIEW_WIDTH) * this.localResolutionBoost;
        const maxHeight = MAX_LIVE_VIEW_HEIGHT + (MAX_LOCAL_VIEW_HEIGHT - MAX_LIVE_VIEW_HEIGHT) * this.localResolutionBoost;
        const scale = Math.min(pixelRatio, maxWidth / viewportWidth, maxHeight / viewportHeight);
        const width = Math.max(2, Math.floor((viewportWidth * scale) / 2) * 2);
        const height = Math.max(2, Math.floor((viewportHeight * scale) / 2) * 2);
        if (this.renderTarget.width !== width || this.renderTarget.height !== height) {
            this.renderTarget.setSize(width, height);
        }
    }
    getFrameInterval() {
        if (this.streaming)
            return 1000 / this.liveViewFPS;
        const maxPixelRatio = Math.min(window.devicePixelRatio, 2);
        const resolutionScale = Math.max(0, Math.min(1, (renderer.getPixelRatio() * this.captureQuality - MIN_AUTO_PIXEL_RATIO) / Math.max(0.01, maxPixelRatio - MIN_AUTO_PIXEL_RATIO)));
        const frameRate = MIN_LIVE_VIEW_FPS + resolutionScale * (MAX_LIVE_VIEW_FPS - MIN_LIVE_VIEW_FPS);
        return 1000 / Math.min(this.liveViewFPS, frameRate);
    }
    getCaptureSize() {
        const scale = Math.min(1, MAX_LIVE_VIEW_WIDTH / this.renderTarget.width, MAX_LIVE_VIEW_HEIGHT / this.renderTarget.height);
        return {
            width: Math.max(2, Math.floor((this.renderTarget.width * scale) / 2) * 2),
            height: Math.max(2, Math.floor((this.renderTarget.height * scale) / 2) * 2)
        };
    }
    renderTexture() {
        renderer.xr.enabled = false;
        renderer.setRenderTarget(null);
        renderer.render(this.scene, this.screenCamera);
    }
    copyRenderTargetToCaptureCanvas() {
        if (this.captureContext === null || this.captureBufferContext === null) {
            throw new Error("Could not create the Live View capture canvas contexts");
        }
        const { width, height } = this.getCaptureSize();
        let readbackTarget = this.renderTarget;
        if (width !== this.renderTarget.width || height !== this.renderTarget.height) {
            if (this.captureRenderTarget.width !== width || this.captureRenderTarget.height !== height) {
                this.captureRenderTarget.setSize(width, height);
            }
            // Downsample the existing mono image instead of rendering the world a second time.
            renderer.setRenderTarget(this.captureRenderTarget);
            renderer.render(this.scene, this.screenCamera);
            readbackTarget = this.captureRenderTarget;
        }
        const requiredPixelCount = width * height * 4;
        if (this.captureBufferCanvas.width !== width || this.captureBufferCanvas.height !== height || this.capturePixels.length !== requiredPixelCount) {
            this.captureCanvas.width = width;
            this.captureCanvas.height = height;
            this.captureBufferCanvas.width = width;
            this.captureBufferCanvas.height = height;
            this.capturePixels = new Uint8Array(requiredPixelCount);
            this.captureImageData = this.captureBufferContext.createImageData(width, height);
        }
        const generation = this.captureReadbackGeneration;
        this.captureReadbackPending = true;
        void renderer
            .readRenderTargetPixelsAsync(readbackTarget, 0, 0, width, height, this.capturePixels)
            .then(() => {
            var _a, _b, _c, _d, _e;
            if (generation !== this.captureReadbackGeneration)
                return;
            const imageData = this.captureImageData;
            if (imageData === undefined)
                return;
            imageData.data.set(this.capturePixels);
            (_a = this.captureBufferContext) === null || _a === void 0 ? void 0 : _a.putImageData(imageData, 0, 0);
            (_b = this.captureContext) === null || _b === void 0 ? void 0 : _b.save();
            (_c = this.captureContext) === null || _c === void 0 ? void 0 : _c.setTransform(1, 0, 0, -1, 0, height);
            try {
                (_d = this.captureContext) === null || _d === void 0 ? void 0 : _d.drawImage(this.captureBufferCanvas, 0, 0);
            }
            finally {
                (_e = this.captureContext) === null || _e === void 0 ? void 0 : _e.restore();
            }
        })
            .catch((error) => {
            console.error("[xr] Failed to read back the Live View frame", error);
        })
            .finally(() => {
            this.captureReadbackPending = false;
        });
    }
    updateCaptureQuality(renderDuration) {
        this.meanRenderDuration = this.meanRenderDuration === 0 ? renderDuration : this.meanRenderDuration * 0.8 + renderDuration * 0.2;
        this.updateFrameRate(performance.now(), this.meanRenderDuration > CAPTURE_QUALITY_DECREASE_THRESHOLD_MS, !this.xrFrameOverBudget && this.meanRenderDuration < CAPTURE_QUALITY_INCREASE_THRESHOLD_MS);
        if (this.meanRenderDuration > CAPTURE_QUALITY_DECREASE_THRESHOLD_MS) {
            this.captureQuality = Math.max(MIN_CAPTURE_QUALITY, this.captureQuality * 0.8);
        }
        else if (this.meanRenderDuration < CAPTURE_QUALITY_INCREASE_THRESHOLD_MS) {
            this.captureQuality = Math.min(1, this.captureQuality + 0.05);
        }
    }
    updateFrameRate(now, overloaded, canIncrease) {
        if (overloaded) {
            this.lastFramePressureAt = now;
            if (now - this.lastFPSAdjustmentAt >= 250) {
                this.liveViewFPS = Math.max(MIN_LIVE_VIEW_FPS, this.liveViewFPS * 0.8);
                this.lastFPSAdjustmentAt = now;
            }
        }
        else if (canIncrease &&
            this.meanRenderDuration < CAPTURE_QUALITY_INCREASE_THRESHOLD_MS &&
            now - this.lastFramePressureAt >= LOCAL_UPGRADE_STABLE_MS &&
            now - this.lastFPSAdjustmentAt >= 1000) {
            this.liveViewFPS = Math.min(MAX_LIVE_VIEW_FPS, this.liveViewFPS + 1);
            this.lastFPSAdjustmentAt = now;
        }
    }
}
